import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Loader2, AlertTriangle } from 'lucide-react';
import Step6Report from '@steps/Step6Report';
import { fetchSharedSession, ApiError } from '@lib/api';
import { useSessionStore } from '@stores/sessionStore';
import { useT, type StringKey } from '@lib/i18n';

type State = 'loading' | 'success' | 'error' | 'not-found';

const PRINT_DELAY_MS = 600;

export default function SharedReport() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const location = useLocation();
  const t = useT();
  const hydrate = useSessionStore((s) => s.hydrateFromShare);

  const [state, setState] = useState<State>('loading');
  const [errMsg, setErrMsg] = useState<string | null>(null);

  // Fetch + hydrate.
  useEffect(() => {
    if (!sessionId) {
      setState('not-found');
      return;
    }
    let cancelled = false;
    fetchSharedSession(sessionId)
      .then((rec) => {
        if (cancelled) return;
        hydrate({
          session: rec.session,
          unit: rec.unit,
          breakdown: rec.breakdown,
          yieldData: rec.yieldData,
          language: rec.language,
        });
        setState('success');
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 404) {
          setState('not-found');
        } else {
          setErrMsg(e instanceof Error ? e.message : String(e));
          setState('error');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId, hydrate]);

  // Auto-print mode (used by Cloudflare Browser Rendering in B.2).
  useEffect(() => {
    if (state !== 'success') return;
    const params = new URLSearchParams(location.search);
    if (params.get('print') !== '1') return;
    const id = window.setTimeout(() => window.print(), PRINT_DELAY_MS);
    return () => window.clearTimeout(id);
  }, [state, location.search]);

  if (state === 'loading') return <LoadingView t={t} />;
  if (state === 'not-found') return <NotFoundView t={t} />;
  if (state === 'error') return <ErrorView t={t} message={errMsg} />;

  return (
    <div>
      <SharedHeader t={t} />
      <Step6Report />
    </div>
  );
}

function SharedHeader({ t }: { t: (k: StringKey) => string }) {
  const today = new Date().toLocaleDateString('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return (
    <header className="bg-navy text-white py-3 px-4 md:px-8 flex items-center justify-between no-print">
      <div className="flex items-center gap-2">
        <span className="w-1.5 h-1.5 rounded-full bg-gold" />
        <span className="text-xs uppercase tracking-wider text-gold-300/80 font-semibold">
          KEY2 BCS
        </span>
        <span className="text-xs text-white/60 ms-2">{t('shared.title')}</span>
      </div>
      <span className="text-xs text-white/50 num-en">{today}</span>
    </header>
  );
}

function LoadingView({ t }: { t: (k: StringKey) => string }) {
  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center gap-3 text-navy-700">
      <Loader2 className="w-10 h-10 animate-spin text-gold" />
      <p className="text-sm md:text-base">{t('shared.loading')}</p>
    </div>
  );
}

function NotFoundView({ t }: { t: (k: StringKey) => string }) {
  return (
    <div className="step-container">
      <div className="card p-8 text-center max-w-lg mx-auto mt-12">
        <div className="w-12 h-12 mx-auto rounded-full bg-gold-50 text-gold-700 flex items-center justify-center mb-3">
          <AlertTriangle className="w-6 h-6" />
        </div>
        <h1 className="text-lg md:text-xl font-bold text-navy mb-2">
          {t('shared.notFoundTitle')}
        </h1>
        <p className="text-sm text-navy-600 leading-relaxed">
          {t('shared.contactAdvisor')}
        </p>
      </div>
    </div>
  );
}

function ErrorView({
  t,
  message,
}: {
  t: (k: StringKey) => string;
  message: string | null;
}) {
  return (
    <div className="step-container">
      <div className="card p-8 text-center max-w-lg mx-auto mt-12">
        <div className="w-12 h-12 mx-auto rounded-full bg-red-50 text-sold flex items-center justify-center mb-3">
          <AlertTriangle className="w-6 h-6" />
        </div>
        <h1 className="text-lg md:text-xl font-bold text-navy mb-2">
          {t('shared.errorTitle')}
        </h1>
        <p className="text-sm text-navy-600 leading-relaxed mb-2">
          {t('shared.contactAdvisor')}
        </p>
        {message && (
          <p className="text-[11px] text-navy-400 num-en break-all">{message}</p>
        )}
      </div>
    </div>
  );
}
